import { useRef } from 'react';
import type { PointerEvent, ReactNode } from 'react';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion';

export interface TiltProps {
  /** Maximum tilt in degrees on each axis. Default 7. */
  maxDeg?: number;
  className?: string;
  children?: ReactNode;
}

const SPRING = { stiffness: 150, damping: 18, mass: 0.6 } as const;

/**
 * 3D mouse-follow tilt for the hero product mock. Pointer position maps
 * to a small rotateX/rotateY (±maxDeg) on a perspective container, with a
 * subtle cursor-following radial sheen (sumi/white, low opacity) on top.
 *
 * Disabled (renders static children, no sheen) under prefers-reduced-motion
 * or on touch / non-fine pointers.
 */
export function Tilt({ maxDeg = 7, className, children }: TiltProps) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  // Normalised pointer position within the element: -0.5 … 0.5
  const px = useMotionValue(0);
  const py = useMotionValue(0);

  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [maxDeg, -maxDeg]), SPRING);
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-maxDeg, maxDeg]), SPRING);

  // Sheen follows the cursor (0–100%).
  const sheenX = useTransform(px, [-0.5, 0.5], ['0%', '100%']);
  const sheenY = useTransform(py, [-0.5, 0.5], ['0%', '100%']);
  const sheen = useMotionTemplate`radial-gradient(60% 60% at ${sheenX} ${sheenY}, hsl(0 0% 100% / 0.14), transparent 60%)`;

  // Detect a fine pointer (mouse/trackpad). Coarse/touch → no tilt.
  const finePointer =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: fine)').matches;

  if (reduced || !finePointer) {
    return <div className={className}>{children}</div>;
  }

  function handleMove(e: PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    px.set((e.clientX - rect.left) / rect.width - 0.5);
    py.set((e.clientY - rect.top) / rect.height - 0.5);
  }

  function handleLeave() {
    px.set(0);
    py.set(0);
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{ perspective: 900 }}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
    >
      <motion.div
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
        className="relative h-full w-full"
      >
        {children}
        {/* Cursor-follow sheen */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit]"
          style={{ background: sheen }}
        />
      </motion.div>
    </div>
  );
}
